import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import React, { useState, useEffect } from "react";
import { compose } from "redux";
import moment from "moment";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import StateSelector from "../InputFields/StateSelector";
import withAPI from "../../services/api";
import FA from "../../containers/fa";

const OrgInfoRow = ({
  fieldName,
  label,
  value,
  patchAPI,
  instanceId,
  canEdit,
  refreshParent,
  settingParams,
  onSettingsSaved,
  api,
}) => {
  const [saveSpin, setSaveSpin] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editableValue, setEditableValue] = useState(value);
  const [permValue, setPermValue] = useState(value);
  const [standardSetChoices, setStandardSetChoices] = useState([]);

  useEffect(() => {
    setPermValue(value);
    setEditableValue(value);
    console.log(
      "OrgInfoRow received settingParams:",
      settingParams,
      " and value:",
      value
    );
    if (fieldName === "preferred_standardset_shortname") {
      fetchStandardSetChoices();
    }
  }, [value, fieldName]);

  const fetchStandardSetChoices = async () => {
    try {
      const response = await api.fetchStandardSets();
      if (response && response.preferred_standard_set_choices) {
        setStandardSetChoices(response.preferred_standard_set_choices);
      }
    } catch (error) {
      console.error("Failed to fetch standard set choices:", error);
    }
  };
  function mapValue(val) {
    if (val === true) {
      return "Yes";
    } else if (val === false) {
      return "No";
    } else if (val instanceof Array) {
      return val.join(", ");
    } else if (
      fieldName === "preferred_standardset_shortname" &&
      standardSetChoices.length > 0
    ) {
      const choice = standardSetChoices.find((c) => c.value === val);
      return choice ? choice.label : val;
    } else {
      return val;
    }
  }
  const handlePatch = () => {
    setSaveSpin(true);
    let data = null;
    // For settings rows, the value that actually lands in Organization.settings
    // (the "None" sentinel means "no value", i.e. null). Held separately from
    // editableValue because it is what onSettingsSaved reports on success.
    let settingsValue = null;

    if (settingParams) {
      settingsValue = editableValue === "None" ? null : editableValue;
      data = { settings: { [fieldName]: settingsValue } };
    } else {
      data = { [fieldName]: editableValue };
    }

    console.log("patchData:", data);

    if (fieldName === "preferred_standardset_shortname") {
      api
        .updateOrganizationPreferredStandardset(instanceId, editableValue)
        .then((resp) => {
          setSaveSpin(false);
          setEditable(false);
          setPermValue(editableValue);
          if (refreshParent) {
            refreshParent();
          }
          console.log("API returned", resp);
        })
        .catch((err) => {
          setSaveSpin(false);
          console.log("API error'd", err);
        });
    } else {
      patchAPI(instanceId, data)
        .then((resp) => {
          if (["state", "district"].includes(fieldName) && refreshParent) {
            refreshParent();
          }
          // Report the saved settings value so the caller can refresh any
          // app-wide cache of org settings, letting other views (e.g. the
          // per-class Lesson Selection View default on my-classes) pick the
          // change up without a re-login. Pass onSettingsSaved only when this
          // row edits the logged-in user's own org: doing so where a superadmin
          // edits some other org would overwrite their own cached settings.
          if (settingParams && onSettingsSaved) {
            onSettingsSaved(fieldName, settingsValue);
          }
          setSaveSpin(false);
          setEditable(false);
          setPermValue(editableValue);
          console.log("API returned", resp);
        })
        .catch((err) => {
          setSaveSpin(false);
          console.log("API error'd", err);
        });
    }
  };

  function currentChoice() {
    return (
      <option key={value} value={value}>
        {value}
      </option>
    );
  }

  function otherChoices() {
    let choices = settingParams
      ? settingParams.choices
      : standardSetChoices.map((c) => c.value);
    let unselectedChoiceValues = choices.filter((v) => v !== value);

    if (settingParams) {
      return unselectedChoiceValues.map((val) => (
        <option key={val} value={val}>
          {val}
        </option>
      ));
    } else {
      return unselectedChoiceValues.map((val) => {
        const choice = standardSetChoices.find((c) => c.value === val);
        return (
          <option key={val} value={val}>
            {choice ? choice.label : val}
          </option>
        );
      });
    }
  }

  function renderChoices() {
    if (fieldName === "preferred_standardset_shortname") {
      return (
        <select
          className=''
          value={editableValue || ""}
          onChange={(e) => {
            setEditableValue(e.target.value);
          }}
        >
          {standardSetChoices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <select
        className=''
        onChange={(e) => {
          setEditableValue(e.target.value);
        }}
      >
        {currentChoice()}
        {value && settingParams.nullValAllowed && (
          <option key={-1} value={null}>
            None
          </option>
        )}
        {otherChoices()}
      </select>
    );
  }

  const handleMultipleChoiceSelection = (event) => {
    console.log(event);
    event.persist();
    setEditableValue(event.target.value);
  };

  function renderMultipleChoice() {
    return (
      <TextField
        select
        style={{ width: "150px" }}
        variant='outlined'
        name={settingParams.name}
        id={settingParams.name}
        label={label}
        SelectProps={{
          multiple: true,
          value: editableValue || [],
          onChange: handleMultipleChoiceSelection,
        }}
      >
        {settingParams.choices.map((choice) => {
          console.log("rendering choice:", choice);
          return <MenuItem value={choice}>{choice}</MenuItem>;
        })}
      </TextField>
    );
  }

  function renderCheckbox() {
    console.log("Rendering checkbox, editableValue:", editableValue);
    return (
      <input
        type='checkbox'
        checked={editableValue}
        onChange={(e) => {
          setEditableValue(!editableValue);
        }}
      />
    );
  }

  function renderTristate() {
    // editableValue is null (Unset) / true (Full lessons) / false (Recommended only).
    // <select> values are strings, so map to/from sentinel strings.
    const current =
      editableValue === true ? "true" : editableValue === false ? "false" : "unset";
    // "Unset" always means "inherit the global default" and is reversible at any time
    // (resolution happens per-learner server-side; there is no destructive cascade),
    // so it must always be selectable.
    return (
      <select
        value={current}
        onChange={(e) => {
          const v = e.target.value;
          setEditableValue(v === "true" ? true : v === "false" ? false : null);
        }}
      >
        {/* Name the effective behavior of "unset" — the global default,
            "Recommended only" — so the option isn't ambiguous. */}
        <option value='unset'>Unset — global setting (Recommended only)</option>
        <option value='true'>Full lessons</option>
        <option value='false'>Recommended only</option>
      </select>
    );
  }

  function renderEditableSetting() {
    if (
      settingParams?.type === "choice" ||
      fieldName === "preferred_standardset_shortname"
    ) {
      return renderChoices();
    } else if (settingParams.type === "boolean") {
      return renderCheckbox();
    } else if (settingParams.type === "tristate") {
      return renderTristate();
    } else if (settingParams.type === "multipleChoice") {
      return renderMultipleChoice();
    } else {
      return null;
    }
  }

  return editable ? (
    <tr key={label}>
      <td>
        <span>{label}</span>
      </td>
      <td>
        {(settingParams || fieldName === "preferred_standardset_shortname") &&
          renderEditableSetting()}

        {!settingParams && fieldName !== "preferred_standardset_shortname" && (
          <React.Fragment>
            {fieldName === "state" ? (
              <StateSelector
                value={editableValue}
                onChangeHandle={(e) => setEditableValue(e.target.value)}
              />
            ) : fieldName === "activated_at" ? (
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DatePicker
                  value={editableValue}
                  onChange={setEditableValue}
                  style={{ marginTop: "16px" }}
                />
              </MuiPickersUtilsProvider>
            ) : (
              <input
                type='text'
                placeholder={permValue}
                value={editableValue}
                onChange={(e) => setEditableValue(e.target.value)}
              />
            )}
          </React.Fragment>
        )}
      </td>
      <td>
        {saveSpin ? (
          <button className='btn btn-sm btn-secondary mr-2'>
            <FA color='white' icon='spinner' spin />
          </button>
        ) : (
          <button
            className='btn btn-sm btn-primary mr-2'
            onClick={() => handlePatch()}
          >
            <FA color='white' icon='save' />
          </button>
        )}
        <button
          className='btn btn-sm btn-danger'
          onClick={() => {
            setEditable(false);
            setEditableValue(permValue);
          }}
        >
          <FA color='white' icon='times' />
        </button>
      </td>
    </tr>
  ) : (
    <tr key={label}>
      <td>
        <span style={{ textTransform: "capitalize" }}>{label}</span>
      </td>
      <td>
        <span>
          {settingParams?.type === "tristate"
            ? permValue === true
              ? "Full lessons"
              : permValue === false
              ? "Recommended only"
              : "Unset — global setting (Recommended only)"
            : fieldName === "activated_at"
            ? permValue
              ? moment.utc(permValue).local().format("MMMM Do YYYY")
              : "-"
            : mapValue(permValue) || "-"}
        </span>
      </td>
      <td>
        {canEdit && (
          <button
            className='btn btn-sm btn-primary'
            onClick={() => setEditable(true)}
          >
            <FA color='white' icon='edit' />
          </button>
        )}
      </td>
    </tr>
  );
};

export default compose(withAPI)(OrgInfoRow);
